import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { gettext } from '@eb/i18n-ebspa';

import Illustration from 'eventbrite_design_system/illustration/Illustration';
import ArrowLeftChunkySVG from 'eventbrite_design_system/iconography/icons/ArrowLeftChunky';
import ArrowRightChunkySVG from 'eventbrite_design_system/iconography/icons/ArrowRightChunky';

import './ScrollPaneWithControls.scss';

const typeL = <ArrowLeftChunkySVG />;
const typeR = <ArrowRightChunkySVG />;
const iconsSize = '40px';
const iconsSizeTop = '16px';
const widthForLargeItemStep = 660;

const getScrollMultiplier = ({ paneWidth, largeItemStep }) =>
    largeItemStep && paneWidth >= widthForLargeItemStep ? largeItemStep : 1;

const Arrow = ({
    shouldShowButton,
    handleClickArrow,
    arrowsSize,
    arrowsOnTop,
    direction,
}) => {
    const leftArrowClassNames = classNames('scrollable-pane__arrow', {
        'scrollable-pane__arrow--left': direction === 'left',
        'scrollable-pane__arrow--right': direction === 'right',
        'scrollable-pane__arrow--disabled': !shouldShowButton,
    });

    let arrowLabel = 'Show more to the right';

    let arrowType = typeR;

    if (direction === 'left') {
        arrowLabel = 'Show more to the left';
        arrowType = typeL;
    }
    let arrow = (
        <div className={leftArrowClassNames}>
            <button
                type="button"
                aria-label={gettext(arrowLabel)}
                onClick={handleClickArrow}
                className="scrollable-pane__arrow-button"
                disabled={!shouldShowButton}
            >
                <Illustration
                    type={arrowType}
                    width={arrowsSize}
                    height={arrowsSize}
                />
            </button>
        </div>
    );

    if (!arrowsOnTop && !shouldShowButton) {
        arrow = null;
    }

    return arrow;
};

const calculateButtonsStatus = ({
    scrollPane,
    sectionContent,
    buttonsShouldBeDisabled,
    numberOfCards,
    cardRef,
}) => {
    let shouldShowFirstButton = false;

    let shouldShowSecondButton = false;

    if (scrollPane && sectionContent) {
        const scrollableArea =
            scrollPane.scrollWidth - sectionContent.clientWidth;

        let cardsOverflowContainer = false;

        if (cardRef) {
            cardsOverflowContainer =
                cardRef.clientWidth * numberOfCards >
                sectionContent.clientWidth;
        }

        shouldShowFirstButton =
            !buttonsShouldBeDisabled && scrollPane.scrollLeft > 0;
        /**
         * When the page loads, the right arrow should be shown only if
         * the scollLeft is the same as the scrollableArea.
         * When scrolling, this means the scollLeft > 0, should show the button until
         * you reach the end and trigger scrollLeft === scrollableArea.
         */
        shouldShowSecondButton =
            !buttonsShouldBeDisabled &&
            cardsOverflowContainer &&
            ((scrollPane.scrollLeft > 0 &&
                scrollPane.scrollLeft !== scrollableArea) ||
                (scrollPane.scrollLeft === 0 &&
                    scrollPane.scrollLeft <= scrollableArea));
    }

    return [shouldShowFirstButton, shouldShowSecondButton];
};

export default class ScrollPaneWithControls extends PureComponent {
    static propTypes = {
        /* Amount of pixel used to move the pane to the sides */
        scrollStep: PropTypes.number,

        // eslint-disable-next-line react/forbid-prop-types
        children: PropTypes.array,

        /* Puts the arrows on top of scrollable pane instead of the sides */
        arrowsOnTop: PropTypes.bool,

        /* Moves more cards per click when on desktop */
        largeItemStep: PropTypes.number,

        /* Allow buttons to be disabled for handling loading states */
        buttonsShouldBeDisabled: PropTypes.bool,
    };

    state = {
        isInBrowser: false,
    };

    componentDidMount() {
        /**
         * Add manual handlers to track user interactions relative to the scrollablePane
         * and browser window.
         */
        this._scrollPane.addEventListener(
            'scroll',
            this._handleManualInteractions,
        );
        window.addEventListener('resize', this._updateDimensions);

        this.setState({
            isInBrowser: true,
        });
    }

    componentWillUnmount() {
        this._scrollPane.removeEventListener(
            'scroll',
            this._handleManualInteractions,
        );
        window.removeEventListener('resize', this._updateDimensions);
    }

    _updateDimensions = () => {
        /**
         * windowWidth state value is not used but this triggers the re-render of the component.
         * forceUpdated was not working.
         * It may be an unused state, but is is necessary to prevent bugs.
         */
        this.setState({
            // eslint-disable-next-line react/no-unused-state
            windowWidth: window.innerWidth,
        });
    };

    /**
     * Generates a re-render of the component without losing track of the actual position.
     * forceUpdated was not working.
     * It may be an unused state, but is is necessary to prevent bugs.
     */
    _handleManualInteractions = () => {
        if (this._scrollPane && this._sectionContent) {
            this.setState({
                // eslint-disable-next-line react/no-unused-state
                manualScrollPosition: this._scrollPane.scrollLeft,
            });
        }
    };

    _onHandleClickLeftArrow = () => {
        const { scrollStep, largeItemStep } = this.props;
        const scrollMultiplier = getScrollMultiplier({
            paneWidth: this._scrollPane.clientWidth,
            largeItemStep,
        });
        const newPosition =
            this._scrollPane.scrollLeft -
            (scrollStep || this._childRef.clientWidth * scrollMultiplier);

        this._scrollPane.scrollTo({ left: newPosition, behavior: 'smooth' });
    };

    _onHandleClickRightArrow = () => {
        const { scrollStep, largeItemStep } = this.props;
        const scrollMultiplier = getScrollMultiplier({
            paneWidth: this._scrollPane.clientWidth,
            largeItemStep,
        });
        const maxScrollPosition =
            this._scrollPane.scrollWidth - this._sectionContent.clientWidth;
        const newPosition =
            this._scrollPane.scrollLeft +
            (scrollStep || this._childRef.clientWidth * scrollMultiplier);

        this._scrollPane.scrollTo({
            left:
                newPosition > maxScrollPosition
                    ? maxScrollPosition
                    : newPosition,
            behavior: 'smooth',
        });
    };

    render() {
        const { children, arrowsOnTop, buttonsShouldBeDisabled } = this.props;
        const scrollPaneRef = (el) => {
            this._scrollPane = el;
        };
        const sectionContentRef = (el) => {
            this._sectionContent = el;
        };
        const childRef = (el) => {
            this._childRef = el;
        };
        const isOnMac =
            this.state.isInBrowser &&
            window.navigator &&
            window.navigator.appVersion.indexOf('Mac') !== -1;
        const scrollPaneClasses = classNames('scrollable-pane', {
            'scroll-pane--mac': isOnMac,
        });
        const arrowsSize = arrowsOnTop ? iconsSizeTop : iconsSize;

        const [
            shouldShowFirstButton,
            shouldShowSecondButton,
        ] = calculateButtonsStatus({
            scrollPane: this._scrollPane,
            sectionContent: this._sectionContent,
            cardRef: this._childRef,
            buttonsShouldBeDisabled,
            numberOfCards: children ? children.length : 0,
        });

        const carouselComponent = (
            <div className="scrollable-pane__wrapper" ref={scrollPaneRef}>
                <div
                    className="scrollable-pane__content"
                    data-spec="categories-section__cards"
                >
                    {children.map((child, index) => (
                        <div key={child.key} ref={index === 0 ? childRef : ''}>
                            {child}
                        </div>
                    ))}
                </div>
            </div>
        );

        const arrowsContainerClasses = classNames('scrollable-pane__arrows', {
            'scrollable-pane__arrows--on-top': arrowsOnTop,
        });

        return (
            <div className={scrollPaneClasses} ref={sectionContentRef}>
                <div className={arrowsContainerClasses}>
                    <Arrow
                        shouldShowButton={shouldShowFirstButton}
                        handleClickArrow={this._onHandleClickLeftArrow}
                        arrowsSize={arrowsSize}
                        arrowsOnTop={arrowsOnTop}
                        direction="left"
                    />
                    <Arrow
                        shouldShowButton={shouldShowSecondButton}
                        handleClickArrow={this._onHandleClickRightArrow}
                        arrowsSize={arrowsSize}
                        arrowsOnTop={arrowsOnTop}
                        direction="right"
                    />
                </div>

                {carouselComponent}
            </div>
        );
    }
}
